import type { ComponentProps, ReactElement, ReactNode } from 'react'
import type { SalesOrderSearchRecord } from '@/lib/services/sales-orders/sales-order.schema'
import type { OrderFilterKey, OrderFilterSelectProps } from '@/components/orders/OrderFilterSelect'
import type { FilterPillOption } from '@/components/patterns/filter-pill-group'
import type {
  IndexTableClearFiltersButtonProps,
  IndexTableSearchBoxProps,
} from '@/components/tables/IndexTable'

type DateChipKey = 'due' | 'due_range' | 'created' | 'created_range'
type ChipKey = DateChipKey | 'customerId'
type ChipFilter =
  | { key: DateChipKey; name: string; format: (value: string) => string }
  | {
      key: 'customerId'
      name: string
      format: (value: NonNullable<SalesOrderSearchRecord['customerId']>) => string
    }
interface FilterContext {
  calendarControls?: ReactNode
  filters: SalesOrderSearchRecord
  implicitFilterKeySet: Set<string>
  layout?: string
  search?: string | null
  onRemoveFilter?: (key: ChipKey) => void
}
interface FilterSummaryProps {
  calendarControls?: ReactNode
  filterBadges: ReactNode[]
  hasClearableFilters: boolean
  onClearFilters?: IndexTableClearFiltersButtonProps['onClick']
  showEmptyLabel?: boolean
}
export interface OrderFilterBarProps {
  calendarControls?: ReactNode
  filters: SalesOrderSearchRecord
  implicitFilterKeys?: string[]
  layout?: string
  onClearFilters?: IndexTableClearFiltersButtonProps['onClick']
  onFilterChange?: OrderFilterSelectProps['onValueChange']
  onRemoveFilter?: (key: ChipKey) => void
  search?: string | null
  showAssigneeOptions?: boolean
}

import CondolencesIcon from '@/assets/CondolencesIcon'
import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import { OrderFilterSelect } from '@/components/orders/OrderFilterSelect'
import { OrdersLayoutSelector } from '@/components/orders/OrdersLayoutSelector'
import {
  IndexTableClearFiltersButton,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { FilterPillGroup } from '@/components/patterns/filter-pill-group'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import {
  DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
  SALES_ORDER_SOURCE_FILTERS,
  salesOrderFilterDefaults,
} from '@/lib/services/sales-orders/sales-order.schema'
import { cn } from '@/lib/utils'
import { Globe, Monitor, Repeat2, ShoppingCart, SlidersHorizontal, X } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

const possibleFilters: ChipFilter[] = [
  {
    key: 'due',
    name: 'Due on',
    format: (date) => dayjs(date).format('M/D/YYYY'),
  },
  {
    key: 'due_range',
    name: 'Due',
    format: (date) => {
      const [start, end] = date.split('to').map((d) => (d ? dayjs(d).format('M/D/YYYY') : '...'))

      return `from ${start} to ${end}`
    },
  },
  {
    key: 'created',
    name: 'Order Date',
    format: (date) => dayjs(date).format('M/D/YYYY'),
  },
  {
    key: 'created_range',
    name: 'Order Date',
    format: (date) => {
      const [start, end] = date.split('to').map((d) => (d ? dayjs(d).format('M/D/YYYY') : '...'))

      return `from ${start} to ${end}`
    },
  },
  {
    key: 'customerId',
    name: 'Customer ID',
    format: (customerId) => `${customerId}`,
  },
]

const selectableFilterKeys: OrderFilterKey[] = [
  'payment_filter',
  'fulfillment_type_filter',
  'fulfillment_status_filter',
  'assignment_filter',
]
const FILTER_BAR_GAP_PX = 8

const sourceFilterOptions: FilterPillOption[] = [
  { value: SALES_ORDER_SOURCE_FILTERS.All, label: 'All', icon: ShoppingCart },
  { value: SALES_ORDER_SOURCE_FILTERS.Web, label: 'Web', icon: Globe },
  { value: SALES_ORDER_SOURCE_FILTERS.Pos, label: 'POS', icon: Monitor },
  { value: SALES_ORDER_SOURCE_FILTERS.Subscriptions, label: 'Subscriptions', icon: Repeat2 },
  { value: SALES_ORDER_SOURCE_FILTERS.Condolences, label: 'Condolences', icon: CondolencesIcon },
  { value: SALES_ORDER_SOURCE_FILTERS.FsnNetwork, label: 'FSN', icon: FsnNetworkIcon },
]

function getFilterLabel(filter: ChipFilter, filters: SalesOrderSearchRecord): string | null {
  if (filter.key === 'customerId') {
    const value = filters.customerId
    return value ? `${filter.name}: ${filter.format(value)}` : null
  }
  const value = filters[filter.key]
  return value ? `${filter.name}: ${filter.format(value)}` : null
}

function getFilterBadges({
  calendarControls,
  filters,
  implicitFilterKeySet,
  layout,
  onRemoveFilter,
}: FilterContext): ReactElement[] {
  return possibleFilters
    .map((filter) => {
      const filterLabel = getFilterLabel(filter, filters)
      if (!filterLabel) {
        return null
      }
      const isImplicitFilter = implicitFilterKeySet.has(filter.key)

      if (layout === 'calendar' && calendarControls && filter.key === 'due_range') {
        return null
      }

      if (isImplicitFilter) {
        return (
          <Badge key={filter.key} variant="filter">
            <span className="font-bold">{filterLabel}</span>
          </Badge>
        )
      }

      return (
        <Tooltip key={filter.key}>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="inline" onClick={() => onRemoveFilter?.(filter.key)}>
              <Badge variant="filter">
                <X className="mr-1" />
                <span className="font-bold">{filterLabel}</span>
              </Badge>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Remove filter</TooltipContent>
        </Tooltip>
      )
    })
    .filter((badge): badge is ReactElement => Boolean(badge))
}

function getFilterState({ filters, implicitFilterKeySet, layout, search }: FilterContext) {
  const hasActiveSearch = Boolean(search?.trim())
  const isImplicitGridQueueFilter =
    layout === 'grid' &&
    filters.fulfillment_status_filter === DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER &&
    implicitFilterKeySet.has('fulfillment_status_filter')
  const activeSelectableFilterCount = selectableFilterKeys.filter(
    (key) =>
      (filters[key] ?? salesOrderFilterDefaults[key]) !== salesOrderFilterDefaults[key] &&
      !implicitFilterKeySet.has(key)
  ).length
  const activeChipFilterCount = possibleFilters.filter(
    (filter) => Boolean(filters[filter.key]) && !implicitFilterKeySet.has(filter.key)
  ).length

  return {
    activeFilterCount:
      activeSelectableFilterCount + activeChipFilterCount + (isImplicitGridQueueFilter ? 1 : 0),
    hasClearableFilters:
      hasActiveSearch ||
      isImplicitGridQueueFilter ||
      activeSelectableFilterCount > 0 ||
      activeChipFilterCount > 0,
  }
}

export function canFitInlineFilterControls({
  containerWidth,
  summaryWidth,
  controlsWidth,
  gapWidth = FILTER_BAR_GAP_PX,
}: {
  containerWidth: number
  summaryWidth: number
  controlsWidth: number
  gapWidth?: number
}): boolean {
  if (!containerWidth || !controlsWidth) {
    return true
  }

  return Math.ceil(summaryWidth) + Math.ceil(controlsWidth) + gapWidth <= Math.floor(containerWidth)
}

function getElementWidth(element: HTMLElement | null): number {
  if (!element) {
    return 0
  }

  return Math.max(element.scrollWidth, element.getBoundingClientRect().width)
}

export function OrderSourceTabs({
  value = SALES_ORDER_SOURCE_FILTERS.All,
  availableSourceFilters,
  onValueChange,
  className,
}: {
  value?: string
  availableSourceFilters?: string[] | null
  onValueChange?: (key: 'source_filter', value: string) => void
  className?: string
}): ReactElement {
  const availableSourceFilterSet = Array.isArray(availableSourceFilters)
    ? new Set([SALES_ORDER_SOURCE_FILTERS.All, ...availableSourceFilters])
    : null
  const visibleSourceFilterOptions = availableSourceFilterSet
    ? sourceFilterOptions.filter((option) => availableSourceFilterSet.has(option.value))
    : sourceFilterOptions

  return (
    <FilterPillGroup
      ariaLabel="Order sources"
      className={className}
      value={value}
      options={visibleSourceFilterOptions}
      onValueChange={(nextValue) => onValueChange?.('source_filter', nextValue)}
    />
  )
}

function ActiveFilterSummary({
  calendarControls,
  filterBadges,
  hasClearableFilters,
  onClearFilters,
  showEmptyLabel = false,
}: FilterSummaryProps): ReactElement | null {
  if (!hasClearableFilters && !filterBadges.length && !calendarControls) {
    if (showEmptyLabel) {
      return (
        <div className="flex h-9 items-center">
          <Typography variant="label" tone="muted">
            Filters:
          </Typography>
        </div>
      )
    }

    return null
  }

  return (
    <div className="inline-flex max-w-full flex-wrap items-center gap-2">
      {hasClearableFilters ? (
        <div className="flex h-9 items-center">
          <IndexTableClearFiltersButton onClick={onClearFilters} />
        </div>
      ) : null}
      {filterBadges}
      {calendarControls}
    </div>
  )
}

export function OrderFilterBar({
  calendarControls,
  filters,
  implicitFilterKeys = [],
  layout,
  onClearFilters,
  onFilterChange,
  onRemoveFilter,
  search,
  showAssigneeOptions = false,
}: OrderFilterBarProps): ReactElement {
  const filterBarContentRef = useRef<HTMLDivElement>(null)
  const filterSummaryRef = useRef<HTMLDivElement>(null)
  const inlineFiltersRef = useRef<HTMLDivElement>(null)
  const [showInlineFilters, setShowInlineFilters] = useState(false)
  const hasCalendarControls = Boolean(calendarControls)
  const implicitFilterKeySet = new Set(implicitFilterKeys)
  const { activeFilterCount, hasClearableFilters } = getFilterState({
    filters,
    implicitFilterKeySet,
    layout,
    search,
  })
  const filterBadges = getFilterBadges({
    calendarControls,
    filters,
    implicitFilterKeySet,
    layout,
    onRemoveFilter,
  })
  const renderSummary = ({ showEmptyLabel = false } = {}) => (
    <ActiveFilterSummary
      calendarControls={calendarControls}
      filterBadges={filterBadges}
      hasClearableFilters={hasClearableFilters}
      onClearFilters={onClearFilters}
      showEmptyLabel={showEmptyLabel}
    />
  )
  const inlineFilterMeasureKey = selectableFilterKeys
    .map((key) => filters[key] ?? salesOrderFilterDefaults[key])
    .join('|')

  useLayoutEffect(() => {
    const filterBarContent = filterBarContentRef.current
    const filterSummary = filterSummaryRef.current
    const inlineFilters = inlineFiltersRef.current

    if (!filterBarContent || !filterSummary || !inlineFilters) {
      return
    }

    let animationFrameId = 0
    const measure = () => {
      const nextShowInlineFilters = canFitInlineFilterControls({
        containerWidth: filterBarContent.clientWidth,
        summaryWidth: getElementWidth(filterSummary),
        controlsWidth: getElementWidth(inlineFilters),
      })

      setShowInlineFilters((current) =>
        current === nextShowInlineFilters ? current : nextShowInlineFilters
      )
    }
    const scheduleMeasure = () => {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = requestAnimationFrame(measure)
    }
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleMeasure)

    measure()
    resizeObserver?.observe(filterBarContent)
    resizeObserver?.observe(filterSummary)
    resizeObserver?.observe(inlineFilters)
    window.addEventListener('resize', scheduleMeasure)

    return () => {
      cancelAnimationFrame(animationFrameId)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [
    filterBadges.length,
    hasCalendarControls,
    hasClearableFilters,
    inlineFilterMeasureKey,
    layout,
    search,
  ])

  return (
    <div className="@container/sales-order-filters rounded-lg border border-border bg-card px-3 py-2">
      <div ref={filterBarContentRef} className="relative flex min-w-0 flex-wrap items-center gap-2">
        <div ref={filterSummaryRef} className="flex max-w-full shrink-0">
          {renderSummary({ showEmptyLabel: true })}
        </div>

        <div
          ref={inlineFiltersRef}
          aria-hidden={showInlineFilters ? undefined : true}
          inert={showInlineFilters ? undefined : true}
          className={cn(
            'flex w-max max-w-full flex-nowrap items-center justify-end gap-2',
            showInlineFilters
              ? 'ml-auto'
              : 'invisible pointer-events-none absolute top-0 right-0 max-w-none'
          )}
        >
          <OrderFilterSelect
            className="flex-nowrap justify-end"
            filters={filters}
            onValueChange={onFilterChange}
            showAssigneeOptions={showAssigneeOptions}
          />
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn('ml-auto w-fit', showInlineFilters && 'hidden')}
            >
              <SlidersHorizontal className="size-4" />
              Filters
              {activeFilterCount > 0 ? (
                <Badge className="ml-1 h-5 min-w-5 justify-center px-1.5">
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full overflow-y-auto sm:max-w-md">
            <SheetHeader className="border-b">
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription className="sr-only">
                Filter orders by date, payment, fulfillment, and assignment.
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-5 px-4 py-5">
              {renderSummary()}
              <div className="space-y-2">
                <div className="text-sm font-medium">Order details</div>
                <OrderFilterSelect
                  className="w-full flex-col items-stretch"
                  filters={filters}
                  onValueChange={onFilterChange}
                  showAssigneeOptions={showAssigneeOptions}
                  triggerClassName="w-full justify-between"
                />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}

function OrderHeader({
  actions,
  layout,
  search,
  numResults,
  isFetching,
  onLayoutChange,
  onSearchCommit,
}: {
  actions?: ReactNode
  layout: ComponentProps<typeof OrdersLayoutSelector>['value']
  search?: IndexTableSearchBoxProps['value']
  numResults: number
  isFetching?: boolean
  onLayoutChange: ComponentProps<typeof OrdersLayoutSelector>['onValueChange']
  onSearchCommit?: IndexTableSearchBoxProps['onCommit']
}): ReactElement {
  const resultsText = numResults === 1 ? '1 result' : `${numResults} results`

  return (
    <IndexTableToolbar
      primary={
        <IndexTableSearchBox
          value={search}
          onCommit={onSearchCommit}
          countText={resultsText}
          isFetching={isFetching}
          placeholder="Search orders..."
        />
      }
      actions={
        <>
          <OrdersLayoutSelector value={layout} onValueChange={onLayoutChange} />
          {actions}
        </>
      }
    />
  )
}

export default OrderHeader
